/** Pure formatting helpers (no React) shared by the view models and ui/kit. */
import { DECISION_LABELS } from '@ward/core';

/** "1 device", "1,204 devices". Pass `String` as `fmt` to print the count without a thousands separator. */
export const plural = (n: number, one: string, many = `${one}s`, fmt: (n: number) => string = (x) => x.toLocaleString()) =>
  `${fmt(n)} ${n === 1 ? one : many}`;

// ── Status vocabulary ─────────────────────────────────────
// One place for the labels and tones of every status the console shows. Backend enum values are the keys;
// labels are what people read. A tone is always paired with text (never color-only).

/** The five tones of the console (styles.css `[data-tone]`). `log` is accepted as a legacy alias of neutral. */
export type Tone = 'block' | 'warn' | 'allow' | 'info' | 'neutral';
export const TONES: readonly Tone[] = ['block', 'warn', 'allow', 'info', 'neutral'];
export const toneOf = (t: string | null | undefined): Tone => (TONES.includes(t as Tone) ? (t as Tone) : 'neutral');

/** `key → [label, tone]`. */
export type StatusMap = Readonly<Record<string, readonly [label: string, tone: Tone]>>;

/** Label for a key of a status map; unknown keys show the raw key. */
export const statusText = (map: StatusMap, key: string | null | undefined): string => (key ? map[key]?.[0] ?? key : '');
export const statusTone = (map: StatusMap, key: string | null | undefined): Tone => (key ? map[key]?.[1] ?? 'neutral' : 'neutral');

/** One severity scale for alerts, policies, extension risk, scan findings and change history. `info` reads as Low. */
export const SEVERITY: StatusMap = {
  critical: ['Critical', 'block'],
  high: ['High', 'block'],
  medium: ['Medium', 'warn'],
  low: ['Low', 'neutral'],
  info: ['Low', 'neutral'],
};
export const severityTone = (s: string | null | undefined) => statusTone(SEVERITY, s);

/** What happened to a user action (event outcome). */
export const OUTCOME: StatusMap = {
  blocked: ['Blocked', 'block'],
  warned_proceeded: ['Warned · continued', 'warn'],
  warned_cancelled: ['Warned · cancelled', 'warn'],
  justified: ['Justified', 'warn'],
  justify_cancelled: ['Justify · cancelled', 'warn'],
  logged: ['Logged', 'neutral'],
  allowed: ['Allowed', 'allow'],
};
export const outcomeTone = (o: string) => statusTone(OUTCOME, o);

/** What a policy decides (labels shared with the policy sentence and explanations in @ward/core). */
export const DECISION: StatusMap = {
  block: [DECISION_LABELS.block, 'block'],
  justify: [DECISION_LABELS.justify, 'warn'],
  warn: [DECISION_LABELS.warn, 'warn'],
  log: [DECISION_LABELS.log, 'neutral'],
  allow: [DECISION_LABELS.allow, 'allow'],
};

export const PROTECTION: StatusMap = {
  protected: ['Protected', 'allow'],
  policy_stale: ['Policy stale', 'warn'],
  extension_outdated: ['Extension outdated', 'warn'],
  disconnected: ['Disconnected', 'block'],
  unprotected: ['Unprotected', 'block'],
  unknown: ['Unknown', 'neutral'],
};

export const ALERT_STATUS: StatusMap = { open: ['Open', 'block'], investigating: ['Investigating', 'warn'], resolved: ['Resolved', 'allow'], dismissed: ['Dismissed', 'neutral'] };
export const ACCOUNT: StatusMap = { corporate: ['Corporate', 'allow'], personal: ['Personal', 'block'], unknown: ['Unrecognised', 'neutral'] };
export const APPROVAL: StatusMap = { sanctioned: ['Sanctioned', 'allow'], unsanctioned: ['Unsanctioned', 'block'], unreviewed: ['Unreviewed', 'neutral'] };
export const EXT_STATUS: StatusMap = { approved: ['Approved', 'allow'], unreviewed: ['Unreviewed', 'neutral'], review: ['Needs review', 'warn'], restricted: ['Restricted', 'warn'], blocked: ['Blocked', 'block'] };
export const SENSITIVITY: StatusMap = { restricted: ['Restricted', 'block'], confidential: ['Confidential', 'warn'], internal: ['Internal', 'info'], public: ['Public', 'neutral'] };

// ── Platform names ────────────────────────────────────────
export const BROWSER_LABEL: Readonly<Record<string, string>> = { edge: 'Microsoft Edge', chrome: 'Google Chrome' };
export const OS_LABEL: Readonly<Record<string, string>> = { mac: 'macOS', win: 'Windows', linux: 'Linux', cros: 'ChromeOS' };
export const browserLabel = (b: string | null | undefined) => (b ? BROWSER_LABEL[b] ?? b : '');
export const osLabel = (o: string | null | undefined) => (o ? OS_LABEL[o] ?? o : '');
